import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { Calendar, MapPin, Building, Trash2, Search, Filter } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;

const ClosuresManager = () => {
  const [closures, setClosures] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [closureTypeFilter, setClosureTypeFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active, cancelled, all

  useEffect(() => {
    fetchClosures();
    fetchProperties();
  }, []);

  const fetchClosures = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/closures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClosures(response.data || []);
    } catch (error) {
      console.error('Error fetching closures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handleCancelClosure = async (closureId, propertyName, startDate, endDate) => {
    if (!window.confirm(`Vuoi annullare il periodo di chiusura di "${propertyName}" dal ${new Date(startDate).toLocaleDateString()} al ${new Date(endDate).toLocaleDateString()}?\n\nNota: La chiusura annullata sarà mantenuta per le statistiche.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      await axios.patch(`${API}/closures/${closureId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Periodo di chiusura annullato con successo!');
      fetchClosures();
    } catch (error) {
      console.error('Error cancelling closure:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'annullamento del periodo di chiusura';
      alert(errorMsg);
    }
  };

  const handleDeleteClosure = async (closureId, propertyName, startDate, endDate) => {
    if (!window.confirm(`ATTENZIONE: Vuoi eliminare DEFINITIVAMENTE il periodo di chiusura di "${propertyName}"?\n\nQuesta azione rimuoverà completamente il record dal database e non può essere annullata.\n\nSe vuoi solo disattivare la chiusura mantenendo i dati per le statistiche, usa "Annulla" invece.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/closures/${closureId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Periodo di chiusura eliminato definitivamente!');
      fetchClosures();
    } catch (error) {
      console.error('Error deleting closure:', error);
      alert('Errore durante l\'eliminazione del periodo di chiusura');
    }
  };

  const getClosureTypeBadge = (type) => {
    const typeConfig = {
      property_initiated: { label: 'Proprietà', className: 'bg-blue-100 text-blue-800' },
      office_initiated: { label: 'Ufficio', className: 'bg-purple-100 text-purple-800' }
    };
    
    const config = typeConfig[type] || typeConfig.property_initiated;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const isCurrentlyClosed = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };

  const getStatusBadge = (startDate, endDate, cancelled) => {
    if (cancelled) {
      return <Badge className="bg-orange-100 text-orange-800">Annullata</Badge>;
    }
    
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (now < start) {
      return <Badge className="bg-yellow-100 text-yellow-800">Programmata</Badge>;
    } else if (now >= start && now <= end) {
      return <Badge className="bg-red-100 text-red-800">In corso</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800">Terminata</Badge>;
    }
  };

  // Get unique values for filters
  const regions = [...new Set(properties.map(p => p.region).filter(Boolean))];
  const cities = [...new Set(properties.map(p => p.city).filter(Boolean))];

  const filteredClosures = closures.filter(closure => {
    const property = properties.find(p => p.id === closure.property_id);
    
    const matchesSearch = !searchTerm || 
      closure.property_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      closure.property_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      closure.reason?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesProperty = propertyFilter === 'all' || closure.property_id === propertyFilter;
    const matchesType = closureTypeFilter === 'all' || closure.closure_type === closureTypeFilter;
    const matchesRegion = regionFilter === 'all' || property?.region === regionFilter;
    const matchesCity = cityFilter === 'all' || property?.city === cityFilter;
    
    // Filter by cancelled status
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = !closure.cancelled;
    } else if (statusFilter === 'cancelled') {
      matchesStatus = closure.cancelled;
    }
    // 'all' shows both active and cancelled
    
    let matchesPeriod = true;
    if (periodFilter !== 'all' && !closure.cancelled) {
      const now = new Date();
      const start = new Date(closure.start_date);
      const end = new Date(closure.end_date);
      
      switch (periodFilter) {
        case 'current':
          matchesPeriod = now >= start && now <= end;
          break;
        case 'upcoming':
          matchesPeriod = start > now;
          break;
        case 'past':
          matchesPeriod = end < now;
          break;
      }
    }
    
    return matchesSearch && matchesProperty && matchesType && matchesRegion && matchesCity && matchesStatus && matchesPeriod;
  });

  if (loading) {
    return (
      <Layout title="Gestione Chiusure">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestione Chiusure">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Periodi di Chiusura</h2>
              <p className="text-sm text-slate-600">Visualizza e gestisci tutti i periodi di chiusura degli immobili</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-900">{filteredClosures.length}</p>
              <p className="text-xs text-slate-500">Periodi totali</p>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="space-y-4">
            {/* First row */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca per nome, codice o motivo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div>
                <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Immobile" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli immobili</SelectItem>
                    {properties.map(property => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.name} ({property.property_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={closureTypeFilter} onValueChange={setClosureTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo chiusura" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    <SelectItem value="property_initiated">Proprietà</SelectItem>
                    <SelectItem value="office_initiated">Ufficio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Attive</SelectItem>
                    <SelectItem value="cancelled">Annullate</SelectItem>
                    <SelectItem value="all">Tutte</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={periodFilter} onValueChange={setPeriodFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Periodo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i periodi</SelectItem>
                    <SelectItem value="current">In corso</SelectItem>
                    <SelectItem value="upcoming">Programmati</SelectItem>
                    <SelectItem value="past">Terminati</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Regione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le regioni</SelectItem>
                    {regions.map(region => (
                      <SelectItem key={region} value={region}>
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Select value={cityFilter} onValueChange={setCityFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Città" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le città</SelectItem>
                    {cities.map(city => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Clear filters */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setPropertyFilter('all');
                  setClosureTypeFilter('all');
                  setPeriodFilter('all');
                  setRegionFilter('all');
                  setCityFilter('all');
                  setStatusFilter('active');
                }}
                className="text-slate-600"
              >
                Cancella Filtri
              </Button>
            </div>
          </div>
        </Card>

        {/* Closures List */}
        <div className="grid gap-4">
          {filteredClosures.length === 0 ? (
            <Card className="p-8 text-center border-0 shadow-lg">
              <Calendar className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Nessun periodo di chiusura trovato</h3>
              <p className="text-slate-500">
                {searchTerm || propertyFilter !== 'all' || closureTypeFilter !== 'all' || periodFilter !== 'all'
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Nessun periodo di chiusura programmato'
                }
              </p>
            </Card>
          ) : (
            filteredClosures.map((closure) => {
              const property = properties.find(p => p.id === closure.property_id);
              return (
                <Card key={closure.id} className="p-4 hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      {/* Header */}
                      <div className="flex items-center space-x-3">
                        <h3 className="text-base font-semibold text-slate-900">{closure.property_name}</h3>
                        <Badge variant="outline" className="text-xs font-mono bg-blue-50 text-blue-700 border-blue-300">
                          {closure.property_code}
                        </Badge>
                        {getClosureTypeBadge(closure.closure_type)}
                        {getStatusBadge(closure.start_date, closure.end_date, closure.cancelled)}
                      </div>

                      {/* Property info */}
                      {property && (
                        <div className="flex items-center text-xs text-slate-600">
                          <MapPin className="h-3 w-3 mr-1" />
                          {property.city}{property.province && ` (${property.province})`}, {property.region}
                        </div>
                      )}

                      {/* Dates and reason */}
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-slate-700">
                          <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                          Dal {new Date(closure.start_date).toLocaleDateString()} al {new Date(closure.end_date).toLocaleDateString()}
                          <span className="ml-2 text-xs text-slate-500">
                            ({Math.ceil((new Date(closure.end_date) - new Date(closure.start_date)) / (1000 * 60 * 60 * 24))} giorni)
                          </span>
                        </div>
                        {closure.reason && (
                          <div className="text-sm text-slate-600">
                            <strong>Motivo:</strong> {closure.reason}
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          Creata da {closure.created_by} il {new Date(closure.created_at).toLocaleDateString()}
                          {closure.cancelled && (
                            <>
                              <br />
                              <span className="text-orange-600 font-medium">
                                Annullata da {closure.cancelled_by} il {new Date(closure.cancelled_at).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex gap-2">
                      {!closure.cancelled ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelClosure(closure.id, closure.property_name, closure.start_date, closure.end_date)}
                            className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                            title="Annulla chiusura (mantiene per statistiche)"
                          >
                            Annulla
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClosure(closure.id, closure.property_name, closure.start_date, closure.end_date)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            title="Elimina definitivamente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClosure(closure.id, closure.property_name, closure.start_date, closure.end_date)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          title="Elimina definitivamente (già annullata)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClosuresManager;